import { z } from "zod";
// "HH:mm" 24h format — validates that hours are 00-23 and minutes are 00-59
const timeStringSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);
// A single time slot (e.g., 08:00–12:00). start must be before end.
export const TimeSlotSchema = z
    .object({
    start: timeStringSchema,
    end: timeStringSchema,
})
    .refine((slot) => slot.start < slot.end, {
    message: "start must be before end",
});
// Working hours per day — ISO 8601 day numbering ("1"=Monday..."7"=Sunday)
// Multiple slots per day supported (e.g., morning + afternoon with lunch break)
// Absent day key = not working that day
// Keys are strings in JSON — validated as "1" through "7"
const dayKeySchema = z.string().regex(/^[1-7]$/);
/** Error message for two overlapping slots on the same day — shared so the
 * frontend can recognise the issue by identity rather than substring match. */
export const WORKING_HOURS_OVERLAP_MESSAGE = "Cette plage en chevauche une autre";
/** Upper bound on slots per day — a sane cap that never constrains real use
 * (a day has at most a handful of windows) while preventing an oversized
 * payload from being persisted. */
export const MAX_SLOTS_PER_DAY = 24;
export const WorkingHoursSchema = z
    .record(dayKeySchema, z.array(TimeSlotSchema).max(MAX_SLOTS_PER_DAY))
    .superRefine((workingHours, ctx) => {
    // Within a single day, no two slots may overlap. Touching boundaries are
    // allowed (e.g. 12:00→13:00 right after 09:00→12:00). The issue is reported
    // on the offending slot's original index so the UI can target it precisely.
    for (const [dayKey, slots] of Object.entries(workingHours)) {
        const ordered = slots
            .map((slot, index) => ({ slot, index }))
            .sort((a, b) => a.slot.start.localeCompare(b.slot.start));
        for (let i = 1; i < ordered.length; i++) {
            const prev = ordered[i - 1];
            const curr = ordered[i];
            if (prev && curr && curr.slot.start < prev.slot.end) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: WORKING_HOURS_OVERLAP_MESSAGE,
                    path: [dayKey, curr.index],
                });
            }
        }
    }
});
// Payload for PUT /api/company-organization-setting
export const UpdateCompanyOrganizationSettingPayloadSchema = z.object({
    workingHours: WorkingHoursSchema,
});
// Payload for PUT /api/member-working-hours/:memberId
export const UpdateMemberWorkingHoursPayloadSchema = z.object({
    workingHoursOverride: WorkingHoursSchema.nullable(),
});
//# sourceMappingURL=planning.types.js.map